"use client"

import { useEffect, useRef, useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import CartDropdownWrapper from "../cart-dropdown-wrapper"
import { usePathname } from "next/navigation"
import NavLine from "../nav-line"

type NavLinksProps = {
  customer: HttpTypes.StoreCustomer | null
  cart: any
}

const NavLinks = ({ customer, cart }: NavLinksProps) => {
  const pathname = usePathname()
  const [activeLink, setActiveLink] = useState<number | null>(null)
  const [linePosition, setLinePosition] = useState({ left: 0, width: 0 })
  const [navItemPositions, setNavItemPositions] = useState<Array<{left: number, width: number}>>([])
  const linkRefs = useRef<(HTMLDivElement | null)[]>([])

  // Determine which page is active based on the pathname
  const getActivePage = () => {
    // Split the pathname to handle localized routes
    const pathParts = pathname.split('/')

    // For localized routes, the first part after the split will be empty, and the second part will be the country code
    // So for a homepage like /hk, pathParts would be ['', 'hk', '']
    // For a store page like /hk/store, pathParts would be ['', 'hk', 'store']

    // Check if we're on the homepage (only country code in the path or country code followed by empty string)
    if (pathParts.length <= 2 || (pathParts.length === 3 && pathParts[2] === '')) {
      return 0
    }

    // Check other pages by looking at the third part of the path (after country code)
    const pagePath = pathParts[2] || ''

    if (pagePath === 'store') return 1
    if (pagePath === 'profile' || pagePath === 'account') return 2
    if (pagePath === 'cart') return 3

    return null
  }

  const activePage = getActivePage()

  // Set initial active link based on current page
  useEffect(() => {
    if (activePage !== null) {
      setActiveLink(activePage)
    }
  }, [activePage])

  // Initialize active link on component mount
  useEffect(() => {
    if (activePage !== null && activeLink === null) {
      setActiveLink(activePage)
    }
  }, [])

  // Update line position when activeLink changes or when refs are set
  useEffect(() => {
    if (activeLink !== null && linkRefs.current[activeLink]) {
      const linkElement = linkRefs.current[activeLink]
      if (linkElement) {
        const { offsetLeft, offsetWidth } = linkElement
        setLinePosition({ left: offsetLeft, width: offsetWidth })
      }
    }
  }, [activeLink, linkRefs.current])

  // Update all line positions
  const updatePositions = () => {
    const positions = linkRefs.current.map(ref => {
      if (ref) {
        const { offsetLeft, offsetWidth } = ref
        return { left: offsetLeft, width: offsetWidth }
      }
      return { left: 0, width: 0 }
    })

    setNavItemPositions(positions)

    // Update active line position
    if (activeLink !== null && linkRefs.current[activeLink]) {
      const { offsetLeft, offsetWidth } = linkRefs.current[activeLink]
      setLinePosition({ left: offsetLeft, width: offsetWidth })
    }
  }

  // Initialize line positions when component mounts and handle window resize
  useEffect(() => {
    const timer = setTimeout(updatePositions, 100)

    // Update positions on window resize
    window.addEventListener('resize', updatePositions)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', updatePositions)
    }
  }, [])



  return (
    <div className="hidden lg:flex ml-auto w-full max-w-[580px] h-full relative">
      {/* Lines for each nav item */}
      {navItemPositions.map((position, index) => {
        // Only show if position is valid
        if (position.width <= 0) {
          return null;
        }

        // Determine if this link is active or hovered
        const isCurrentlyActive = index === activePage;

        return (
          <NavLine
            key={index}
            isActive={isCurrentlyActive}
            isHovered={index === activeLink && !isCurrentlyActive}
            isClicked={false}
            left={position.left}
            width={position.width}
          />
        );
      })}



      {/* Links */}
      <div
        ref={el => linkRefs.current[0] = el}
        className="flex-1 h-full"
      >
        <LocalizedClientLink
          href="/"
          className="flex items-center justify-start h-full w-full px-2"
          data-testid="nav-home-link"

          onMouseEnter={() => setActiveLink(0)}
          onMouseLeave={() => setActiveLink(null)}
        >
          <span className="w-full text-white hover:text-gray-300 py-1 flex items-center justify-start">
            Home
          </span>
        </LocalizedClientLink>
      </div>

      <div
        ref={el => linkRefs.current[1] = el}
        className="flex-1 h-full"
      >
        <LocalizedClientLink
          href="/store"
          className="flex items-center justify-start h-full w-full px-2"
          data-testid="nav-store-link"

          onMouseEnter={() => setActiveLink(1)}
          onMouseLeave={() => setActiveLink(null)}
        >
          <span className="w-full text-white hover:text-gray-300 py-1 flex items-center justify-start">
            Products
          </span>
        </LocalizedClientLink>
      </div>

      <div
        ref={el => linkRefs.current[2] = el}
        className="flex-1 h-full"
      >
        {customer ? (
          <LocalizedClientLink
            href="/profile"
            className="flex items-center justify-start h-full w-full px-2"
            data-testid="nav-profile-link"

            onMouseEnter={() => setActiveLink(2)}
            onMouseLeave={() => setActiveLink(null)}
          >
            <span className="w-full text-white hover:text-gray-300 py-1 flex items-center justify-start">
              Profile
            </span>
          </LocalizedClientLink>
        ) : (
          <LocalizedClientLink
            href="/account"
            className="flex items-center justify-start h-full w-full px-2"
            data-testid="nav-signin-link"

            onMouseEnter={() => setActiveLink(2)}
            onMouseLeave={() => setActiveLink(null)}
          >
            <span className="w-full text-white hover:text-gray-300 py-1 flex items-center justify-start">
              Sign In
            </span>
          </LocalizedClientLink>
        )}
      </div>

      <div
        ref={el => linkRefs.current[3] = el}
        className="flex-1 h-full"
        onMouseEnter={() => setActiveLink(3)}
        onMouseLeave={() => setActiveLink(null)}
      >
        <div className="h-full w-full px-2">
          <CartDropdownWrapper cart={cart} />
        </div>
      </div>
    </div>
  )
}

export default NavLinks
